import { useInfiniteQuery } from '@tanstack/react-query';

import { stringToDateRange } from '@/helpers';
import { FilterValues } from '@/hooks/use-filter-values';
import {
  DEFAULT_PAGE_SIZE,
  flattenInfinitePages,
  getNextPageParam,
} from '@/lib/constants/pagination';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getTeamMemberChanges } from '../api/team.api';
import { mapTeamMemberChangeTypeToDTO } from '../mappers/team-member-change-section.mapper';
import { TeamMemberChangeType } from '../types/team-member-changes';

const mapTeamMemberChangesFilters = (filters: FilterValues['values'], teamMemberId: number) => {
  const apiFilters: any = {
    teamMemberId,
  };

  if (filters.changeType) {
    const changeTypes = Array.isArray(filters.changeType)
      ? filters.changeType
      : [filters.changeType];
    apiFilters.sections = changeTypes.map(type =>
      mapTeamMemberChangeTypeToDTO(type as TeamMemberChangeType)
    );
  }

  if (filters.dateRange) {
    const dateRange = stringToDateRange(filters.dateRange as string);
    if (dateRange?.from) {
      apiFilters.fromDate = normalizeToStartOfDayUTC(dateRange.from).toISOString();
    }
    if (dateRange?.to) {
      apiFilters.toDate = normalizeToStartOfDayUTC(dateRange.to).toISOString();
    }
  }

  if (filters.changedBy) {
    const changedByValues = Array.isArray(filters.changedBy)
      ? filters.changedBy
      : [filters.changedBy];
    apiFilters.changedByUserIds = changedByValues.map(Number);
  }

  return apiFilters;
};

interface UseTeamMemberChangesParams {
  teamMemberId: number;
  filters?: FilterValues['values'];
  enabled?: boolean;
}

export const useTeamMemberChanges = ({
  teamMemberId,
  filters = {},
  enabled = true,
}: UseTeamMemberChangesParams) => {
  return useInfiniteQuery({
    queryKey: ['team-member-changes', teamMemberId, filters],
    queryFn: ({ pageParam }) => {
      const requestFilters = mapTeamMemberChangesFilters(filters, teamMemberId);
      return getTeamMemberChanges({
        ...requestFilters,
        pagination: {
          pageSize: DEFAULT_PAGE_SIZE,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - changes don't update frequently during a session
    select: flattenInfinitePages,
  });
};
